import React, { useEffect, useState } from 'react';
import { ArrowRight, Star, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import axios from 'axios';

const Hero = () => {
  const { t, i18n } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch hero slides from API
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hero-slides`);
        if (response.data && response.data.length > 0) {
          setHeroSlides(response.data);
        } else {
          // Fallback slides if API fails
          setHeroSlides([
            {
              id: 1,
              image: "https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1920&q=80",
              title: "Créez des Moments Magiques",
              title_en: "Create Magical Moments",
              subtitle: "Transformez vos événements avec nos décorations de ballons sur mesure",
              subtitle_en: "Transform your events with our custom balloon decorations"
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching hero slides:', error);
        // Fallback slides
        setHeroSlides([
          {
            id: 1,
            image: "https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1920&q=80",
            title: "Créez des Moments Magiques",
            title_en: "Create Magical Moments",
            subtitle: "Transformez vos événements avec nos décorations de ballons sur mesure",
            subtitle_en: "Transform your events with our custom balloon decorations"
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, []);

  // Auto-advance slider
  useEffect(() => {
    if (heroSlides.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 10000); // 10 seconds per slide

    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  if (loading || heroSlides.length === 0) {
    return <div className="min-h-screen bg-gradient-to-br from-orange-100 via-pink-100 to-fuchsia-100"></div>;
  }

  const currentSlideData = heroSlides[currentSlide];
  const displayTitle = i18n.language === 'en' ? (currentSlideData.title_en || currentSlideData.title) : currentSlideData.title;
  const displaySubtitle = i18n.language === 'en' ? (currentSlideData.subtitle_en || currentSlideData.subtitle) : currentSlideData.subtitle;

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image Slider */}
      <div className="absolute inset-0">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image.startsWith('/api/') 
                ? `${process.env.REACT_APP_BACKEND_URL}${slide.image}` 
                : slide.image}
              alt={displayTitle}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
          </div>
        ))}
      </div>

      {/* Slider Controls */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>

      {/* Slider Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentSlide
                ? 'bg-white w-8'
                : 'bg-white/50 hover:bg-white/75'
            }`}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center pt-32">
          {/* Left Content */}
          <div className="text-left space-y-8">
            <h1 className="text-5xl lg:text-7xl font-bold leading-tight drop-shadow-2xl">
              <span className="bg-gradient-to-r from-white via-yellow-100 to-orange-200 bg-clip-text text-transparent drop-shadow-lg">
                {displayTitle}
              </span>
            </h1>

            <p className="text-xl text-white max-w-xl leading-relaxed drop-shadow-lg font-medium">
              {displaySubtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-gradient-to-r from-orange-500 via-pink-600 to-fuchsia-600 hover:from-orange-600 hover:via-pink-700 hover:to-fuchsia-700 text-white px-8 py-6 rounded-full shadow-xl hover:shadow-2xl transition-all text-lg group"
              >
                {t('hero.cta1')}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection('gallery')}
                size="lg"
                variant="outline"
                className="border-2 border-white bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-6 rounded-full shadow-xl text-lg"
              >
                <Star className="mr-2 w-5 h-5" />
                {t('hero.cta2')}
              </Button>
            </div>
          </div>

          {/* Right Content - Featured Stats */}
          <div className="hidden lg:grid grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all">
              <div className="text-5xl font-bold bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent mb-2">
                500+
              </div>
              <p className="text-white font-semibold">Événements Réalisés</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all">
              <div className="text-5xl font-bold bg-gradient-to-r from-pink-300 to-fuchsia-400 bg-clip-text text-transparent mb-2">
                5.0
              </div>
              <p className="text-white font-semibold">Note Moyenne</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all">
              <div className="text-5xl font-bold bg-gradient-to-r from-orange-300 to-pink-400 bg-clip-text text-transparent mb-2">
                100%
              </div>
              <p className="text-white font-semibold">Satisfaction Client</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all">
              <div className="text-5xl font-bold bg-gradient-to-r from-fuchsia-300 to-purple-400 bg-clip-text text-transparent mb-2">
                24h
              </div>
              <p className="text-white font-semibold">Réponse Garantie</p>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Decorative Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <Sparkles className="absolute top-20 right-20 w-8 h-8 text-yellow-300 animate-pulse" />
        <Sparkles className="absolute bottom-40 left-10 w-6 h-6 text-pink-300 animate-pulse delay-100" />
        <Sparkles className="absolute top-1/3 left-1/4 w-5 h-5 text-fuchsia-300 animate-pulse delay-200" />
      </div>
    </section>
  );
};

export default Hero;
